<template>
	<div class="p-6">
		<div class="flex justify-between items-start mb-6 flex-wrap gap-4">
			<div>
				<h1 class="text-2xl font-semibold text-gray-800 m-0">Discount Vouchers</h1>
				<p class="text-gray-500 mt-1 mb-0">View and manage all voucher codes across discounts</p>
			</div>
			<div class="flex gap-2">
				<button
					:disabled="selectedVouchers.length === 0"
					class="inline-flex items-center gap-1.5 py-2 px-3 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
					@click="exportSelected"
				>
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
					</svg>
					Export Selected ({{ selectedVouchers.length }})
				</button>
				<button
					class="inline-flex items-center gap-1.5 py-2 px-3 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-orange-6"
					@click="exportAll"
				>
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
					</svg>
					Export All
				</button>
			</div>
		</div>

		<div class="bg-white rounded-lg shadow-sm overflow-hidden">
			<div class="p-4 px-6 border-b border-gray-200">
				<div class="flex flex-wrap items-center justify-between gap-4">
					<div class="flex items-center gap-2">
						<span class="font-semibold text-gray-700">Vouchers</span>
						<span class="bg-orange-2 text-orange-7 py-0.5 px-2 rounded-full text-xs font-medium">
							{{ filteredVouchers.length }} total
						</span>
					</div>
					<div class="flex items-center gap-3">
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search by code..."
								class="py-2 px-3 pl-9 border border-gray-300 rounded-md text-sm w-52 outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
							>
						</div>
						<button
							class="inline-flex items-center gap-1.5 py-2 px-3 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
							@click="refreshData"
						>
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
							Refresh
						</button>
					</div>
				</div>
			</div>

			<div v-if="loading" class="flex justify-center items-center p-12">
				<div class="w-8 h-8 border-2 border-gray-200 border-t-orange-7 rounded-full animate-spin"></div>
			</div>

			<div v-else-if="filteredVouchers.length === 0" class="text-center p-12 text-gray-500">
				<svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
				</svg>
				<p class="m-0">No vouchers found</p>
			</div>

			<div v-else class="overflow-x-auto">
				<table class="w-full border-collapse min-w-[1000px]">
					<thead>
						<tr class="bg-gray-50 border-b border-gray-200">
							<th class="py-3 px-4 text-center w-10">
								<input
									type="checkbox"
									:checked="allSelected"
									:indeterminate="someSelected && !allSelected"
									class="w-4 h-4 accent-orange-7"
									@change="toggleSelectAll"
								>
							</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Code</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Campaign</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Referrer</th>
							<th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Valid</th>
							<th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Used</th>
							<th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Claimed</th>
							<th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Limit</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Start</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">End</th>
							<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order IDs</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="voucher in paginatedVouchers"
							:key="voucher.id"
							class="border-b border-gray-200 transition-colors"
							:class="isSelected(voucher.id) ? 'bg-orange-2' : 'hover:bg-gray-50'"
						>
							<td class="py-3 px-4 text-center">
								<input
									type="checkbox"
									:checked="isSelected(voucher.id)"
									class="w-4 h-4 accent-orange-7"
									@change="toggleSelect(voucher)"
								>
							</td>
							<td class="py-3 px-4 text-sm text-gray-500">{{ voucher.id }}</td>
							<td class="py-3 px-4 text-sm font-medium text-gray-900 font-mono">{{ voucher.code }}</td>
							<td class="py-3 px-4 text-sm text-gray-700">{{ voucher.campaignName || '-' }}</td>
							<td class="py-3 px-4 text-sm text-gray-500">{{ voucher.referrerid || '-' }}</td>
							<td class="py-3 px-4 text-center">
								<span
									v-if="voucher.validity"
									class="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full"
								>
									<svg class="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
										<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
									</svg>
								</span>
								<span
									v-else
									class="inline-flex items-center justify-center w-6 h-6 bg-red-100 rounded-full"
								>
									<svg class="w-3.5 h-3.5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
										<path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
									</svg>
								</span>
							</td>
							<td class="py-3 px-4 text-center text-sm text-gray-700">{{ voucher.used }}</td>
							<td class="py-3 px-4 text-center text-sm text-gray-700">{{ voucher.claimed }}</td>
							<td class="py-3 px-4 text-center text-sm text-gray-700">{{ voucher.limit }}</td>
							<td class="py-3 px-4 text-sm text-gray-500">{{ voucher.startdate }}</td>
							<td class="py-3 px-4 text-sm text-gray-500">{{ voucher.enddate }}</td>
							<td class="py-3 px-4 text-sm text-gray-500 max-w-36 overflow-hidden text-ellipsis whitespace-nowrap" :title="voucher.orderIds">
								{{ voucher.orderIds || '-' }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div v-if="filteredVouchers.length > 0" class="flex flex-wrap items-center justify-between py-3 px-6 border-t border-gray-200 gap-4">
				<div class="flex items-center gap-2">
					<span class="text-sm text-gray-500">Rows per page:</span>
					<select
						v-model="pageSize"
						class="py-1 px-2 border border-gray-300 rounded text-sm outline-none"
					>
						<option :value="10">10</option>
						<option :value="25">25</option>
						<option :value="50">50</option>
						<option :value="100">100</option>
					</select>
				</div>
				<div class="flex items-center gap-2">
					<span class="text-sm text-gray-500">
						{{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, filteredVouchers.length) }} of {{ filteredVouchers.length }}
					</span>
					<button
						:disabled="currentPage === 1"
						class="p-1.5 border border-gray-300 rounded bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						@click="currentPage--"
					>
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
						</svg>
					</button>
					<button
						:disabled="currentPage >= totalPages"
						class="p-1.5 border border-gray-300 rounded bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						@click="currentPage++"
					>
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
						</svg>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { DiscountVoucherModel } from '@sosocio/creator/interfaces';
import DiscountVoucherService from 'services/DiscountVoucherService';
import Swal from 'sweetalert2';

interface ExtendedVoucherModel extends DiscountVoucherModel {
	campaignName?: string;
	orderIds?: string;
	remark?: string;
}

const showToast = (message: string, icon: 'success' | 'error' = 'success') => {
	Swal.fire({
		toast: true,
		position: 'top-end',
		icon,
		title: message,
		showConfirmButton: false,
		timer: 3000,
		timerProgressBar: true,
		customClass: {
			popup: 'windmill-swal-toast',
		},
	});
};

export default defineComponent({
	name: 'DiscountVouchersPage',
	setup() {
		const vouchers = ref<ExtendedVoucherModel[]>([]);
		const selectedVouchers = ref<ExtendedVoucherModel[]>([]);
		const loading = ref(false);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(25);
		
		const filteredVouchers = computed(() => {
			if (!searchQuery.value) return vouchers.value;
			const query = searchQuery.value.toLowerCase();
			return vouchers.value.filter(v =>
				v.code?.toLowerCase().includes(query) ||
				v.campaignName?.toLowerCase().includes(query)
			);
		});
		
		const totalPages = computed(() => Math.ceil(filteredVouchers.value.length / pageSize.value));
		
		const paginatedVouchers = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredVouchers.value.slice(start, start + pageSize.value);
		});
		
		const allSelected = computed(() => {
			return paginatedVouchers.value.length > 0 && paginatedVouchers.value.every(v => isSelected(v.id));
		});
		
		const someSelected = computed(() => {
			return paginatedVouchers.value.some(v => isSelected(v.id));
		});
		
		const isSelected = (id: number) => {
			return selectedVouchers.value.some(v => v.id === id);
		};
		
		const toggleSelect = (voucher: ExtendedVoucherModel) => {
			const index = selectedVouchers.value.findIndex(v => v.id === voucher.id);
			if (index >= 0) {
				selectedVouchers.value.splice(index, 1);
			} else {
				selectedVouchers.value.push(voucher);
			}
		};
		
		const toggleSelectAll = () => {
			if (allSelected.value) {
				paginatedVouchers.value.forEach(v => {
					const index = selectedVouchers.value.findIndex(sv => sv.id === v.id);
					if (index >= 0) selectedVouchers.value.splice(index, 1);
				});
			} else {
				paginatedVouchers.value.forEach(v => {
					if (!isSelected(v.id)) selectedVouchers.value.push(v);
				});
			}
		};
		
		const fetchData = async () => {
			loading.value = true;
			try {
				const params = new URLSearchParams({
					fields: 'id,code,referrerid,discountid,campaignName,remark,orderIds,validity,startdate,enddate,single,credit_absolute,credit_quantity,used,limit,claimed',
					limit: '0',
				});
				const response = await DiscountVoucherService.getAll(params);
				vouchers.value = response;
			} catch (error: any) {
				showToast(error.message || 'Failed to load vouchers', 'error');
			} finally {
				loading.value = false;
			}
		};
		
		const refreshData = () => {
			fetchData();
		};
		
		const exportToCsv = (rows: ExtendedVoucherModel[], filename: string) => {
			const headers = ['id', 'code', 'campaignName', 'referrerid', 'validity', 'used', 'claimed', 'limit', 'startdate', 'enddate', 'orderIds'];
			const csvContent = [
				headers.join(','),
				...rows.map(row => {
					return headers.map(h => {
						const val = row[h as keyof ExtendedVoucherModel];
						if (val === null || val === undefined) return '';
						const str = String(val);
						if (str.includes(',') || str.includes('"') || str.includes('\n')) {
							return `"${str.replace(/"/g, '""')}"`;
						}
						return str;
					}).join(',');
				})
			].join('\n');
			
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = filename;
			link.click();
			URL.revokeObjectURL(link.href);
		};
		
		const exportSelected = () => {
			if (selectedVouchers.value.length === 0) return;
			exportToCsv(selectedVouchers.value, 'selected_vouchers.csv');
			showToast(`Exported ${selectedVouchers.value.length} vouchers`);
		};
		
		const exportAll = () => {
			exportToCsv(filteredVouchers.value, 'all_vouchers.csv');
			showToast(`Exported ${filteredVouchers.value.length} vouchers`);
		};
		
		onMounted(() => {
			fetchData();
		});
		
		return {
			vouchers,
			selectedVouchers,
			loading,
			searchQuery,
			currentPage,
			pageSize,
			filteredVouchers,
			totalPages,
			paginatedVouchers,
			allSelected,
			someSelected,
			isSelected,
			toggleSelect,
			toggleSelectAll,
			refreshData,
			exportSelected,
			exportAll,
		};
	},
});
</script>
